import React from "react";
import { Box, MenuItem } from "@mui/material";
import { useAuthState } from "react-firebase-hooks/auth";
import { t } from "i18next";
import { auth } from "../firebase";
import useUnreadMessageCount from "./QueryHooks/useUnreadMessageCount";
import useListSearchCriteria from "./QueryHooks/useListSearchCriteria";
import { useIsModerator } from "./QueryHooks/useModeration";
import { features } from "../utils/features";
import { CustomSettingsIcon } from "../components/CustomIcons";
import CountBadge from "../components/CountBadge";

export type MenuLink = { label: string; path: string; icon?: JSX.Element; badge?: number };

/**
 * The account menu, shared by the desktop header and the phone's bottom bar so both offer the same
 * pages. Each group is shown between dividers; the callers add signing in or out themselves.
 */
export default function useAccountMenu() {
  const [user] = useAuthState(auth);
  const { data: unreadCount } = useUnreadMessageCount(!!user && features.messages);
  const { data: moderator } = useIsModerator(!!user && features.admin);
  const isModerator = features.admin && !!moderator?.isAdmin;
  const { data: savedCriteria } = useListSearchCriteria(!!user);
  const savedCount = Array.isArray(savedCriteria)
    ? savedCriteria.filter((s: any) => !s.isDeleted).length
    : 0;

  const groups: MenuLink[][] = React.useMemo(() => {
    const otherPages: MenuLink[] = [
      ...(features.agencies ? [{ label: "Strony", path: "/agency-directory" }] : []),
      { label: "Zgłoś agencję", path: "/add-site" },
      { label: "Napisz do nas", path: "/contact" },
      ...(isModerator
        ? [
            { label: "Moderacja ogłoszeń", path: "/admin/listings" },
            { label: "Diagnostyka stron", path: "/admin/sites" },
          ]
        : []),
    ];
    if (!user) return [otherPages];

    return [
      [
        { label: "Szukaj", path: "/" },
        ...(features.marketplace
          ? [
              { label: "Znajdź kupujących", path: "/sell" },
              { label: "Moje ogłoszenia", path: "/my-listings" },
            ]
          : []),
        { label: t("saved", { ns: "words", defaultValue: "Zapisane" }), path: "/subscriptions", badge: savedCount || undefined },
        ...(features.messages
          ? [{ label: t("messages", { ns: "words", defaultValue: "Wiadomości" }), path: "/conversations", badge: unreadCount || undefined }]
          : []),
        { label: t("favorite", { ns: "words", defaultValue: "Ulubione" }), path: "/favorite" },
        { label: t("history", { ns: "words", defaultValue: "Historia" }), path: "/history" },
        ...(features.marketplace ? [{ label: t("wallet", { ns: "words", defaultValue: "Portfel" }), path: "/wallet" }] : []),
      ],
      otherPages,
      [{ label: t("settings", { ns: "words", defaultValue: "Ustawienia" }), path: "/settings", icon: <CustomSettingsIcon /> }],
    ];
  }, [user, savedCount, unreadCount, isModerator]);

  return { groups, savedCount, unreadCount };
}

/** Leaves out the pages the caller's own navigation already shows, and any group left empty. */
export const withoutPaths = (groups: MenuLink[][], paths: string[]) =>
  groups.map((group) => group.filter((link) => !paths.includes(link.path))).filter((group) => group.length > 0);

/** The groups as menu items; an array rather than a fragment, because MUI's Menu needs its items as direct children. */
export const renderMenuGroups = (groups: MenuLink[][], onSelect: (path: string) => void) =>
  groups.flatMap((group, index) => [
    ...(index > 0 ? [<Box key={`divider-${index}`} sx={{ height: "1px", bgcolor: "var(--line)", my: 0.5 }} />] : []),
    ...group.map((link) => (
      <MenuItem key={link.path} onClick={() => onSelect(link.path)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
          {link.icon}
          <Box component="span" sx={{ flex: 1 }}>{link.label}</Box>
          {link.badge ? <CountBadge count={link.badge} /> : null}
        </Box>
      </MenuItem>
    )),
  ]);
